import { Component, ReactNode } from 'react';
import { Card } from './Card';
import { AlertTriangle } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  showDebug?: boolean;
  debugData?: Record<string, any>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: any;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI with debug information
      return (
        <div className="container mx-auto px-4 py-8">
          <Card className="max-w-4xl mx-auto bg-red-900/20 border-red-500 border-2 p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-6 w-6 text-red-500" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-red-400 mb-2">
                  Something went wrong
                </h2>
                <p className="text-gray-300 mb-4">
                  The page encountered an error and couldn't render properly.
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  Reload Page
                </button>
              </div>
            </div>

            {/* Debug Information */}
            {this.props.showDebug && (
              <div className="space-y-4 mt-6 pt-6 border-t border-red-500/30">
                <h3 className="text-lg font-semibold text-red-400">
                  🐛 Debug Information
                </h3>

                {/* Error Details */}
                <div>
                  <h4 className="text-white font-semibold mb-2">Error:</h4>
                  <pre className="bg-gray-900 text-red-400 p-3 rounded overflow-auto max-h-40 text-sm">
                    {this.state.error?.toString()}
                  </pre>
                </div>

                {/* Stack Trace */}
                {this.state.error?.stack && (
                  <div>
                    <h4 className="text-white font-semibold mb-2">Stack Trace:</h4>
                    <pre className="bg-gray-900 text-gray-400 p-3 rounded overflow-auto max-h-60 text-xs">
                      {this.state.error.stack}
                    </pre>
                  </div>
                )}

                {/* Component Stack */}
                {this.state.errorInfo?.componentStack && (
                  <div>
                    <h4 className="text-white font-semibold mb-2">Component Stack:</h4>
                    <pre className="bg-gray-900 text-gray-400 p-3 rounded overflow-auto max-h-40 text-xs">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </div>
                )}

                {/* Additional Debug Data */}
                {this.props.debugData && Object.keys(this.props.debugData).length > 0 && (
                  <div>
                    <h4 className="text-white font-semibold mb-2">Additional Debug Data:</h4>
                    {Object.entries(this.props.debugData).map(([key, value]) => (
                      <div key={key} className="mb-3">
                        <h5 className="text-yellow-500 font-medium mb-1">{key}:</h5>
                        <pre className="bg-gray-900 text-green-400 p-2 rounded overflow-auto max-h-40 text-xs">
                          {JSON.stringify(value, null, 2)}
                        </pre>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
